import { Plus, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { SmileNavIcon } from '@/components/help-center/MaskedSvgIcon';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { cn } from '@/lib/utils';

type SmilieCodeFieldProps = {
  id?: string;
  label?: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
};

/** Input de código de smiley com pré-visualização GsImage (estilo footer peek). */
export function SmilieCodeField({
  id,
  label,
  value,
  onChange,
  placeholder,
  className,
}: SmilieCodeFieldProps) {
  const code = value.trim() || placeholder?.trim() || '';

  return (
    <div className={cn('grid gap-2', className)}>
      {label ? (
        <Label htmlFor={id} className="text-blue-100/90">
          {label}
        </Label>
      ) : null}
      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2">
        <GsImage
          code={code || 'smile'}
          className="flex h-10 w-10 shrink-0 items-center justify-center"
          imgClassName="max-h-9 max-w-9 object-contain"
        />
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(painelInputClass, 'min-w-0 flex-1 font-mono text-xs')}
        />
      </div>
    </div>
  );
}

type DashedAddAccent = 'sky' | 'amber' | 'cyan' | 'violet' | 'pink' | 'emerald';

const ADD_ACCENT: Record<
  DashedAddAccent,
  { shell: string; icon: string; label: string }
> = {
  sky: {
    shell: 'border-white/15 bg-white/[0.02] hover:border-sky-400/50 hover:bg-sky-400/[0.06]',
    icon: 'border-white/15 bg-white/[0.04] text-blue-100/70 group-hover:border-sky-400/50 group-hover:bg-sky-400/15 group-hover:text-sky-200',
    label: 'text-blue-100/50 group-hover:text-sky-200/90',
  },
  amber: {
    shell:
      'border-amber-400/40 bg-amber-500/[0.07] hover:border-amber-400/65 hover:bg-amber-500/[0.14]',
    icon: 'border-amber-400/35 bg-amber-500/10 text-amber-300 group-hover:border-amber-300/55 group-hover:bg-amber-500/20 group-hover:text-amber-200',
    label: 'text-amber-200/75 group-hover:text-amber-100',
  },
  cyan: {
    shell:
      'border-cyan-400/40 bg-cyan-500/[0.07] hover:border-cyan-400/65 hover:bg-cyan-500/[0.14]',
    icon: 'border-cyan-400/35 bg-cyan-500/10 text-cyan-300 group-hover:border-cyan-300/55 group-hover:bg-cyan-500/20 group-hover:text-cyan-200',
    label: 'text-cyan-200/75 group-hover:text-cyan-100',
  },
  violet: {
    shell:
      'border-violet-400/40 bg-violet-500/[0.07] hover:border-violet-400/65 hover:bg-violet-500/[0.14]',
    icon: 'border-violet-400/35 bg-violet-500/10 text-violet-300 group-hover:border-violet-300/55 group-hover:bg-violet-500/20 group-hover:text-violet-200',
    label: 'text-violet-200/75 group-hover:text-violet-100',
  },
  pink: {
    shell:
      'border-pink-400/40 bg-pink-500/[0.07] hover:border-pink-400/65 hover:bg-pink-500/[0.14]',
    icon: 'border-pink-400/35 bg-pink-500/10 text-pink-300 group-hover:border-pink-300/55 group-hover:bg-pink-500/20 group-hover:text-pink-200',
    label: 'text-pink-200/75 group-hover:text-pink-100',
  },
  emerald: {
    shell:
      'border-emerald-400/40 bg-emerald-500/[0.07] hover:border-emerald-400/65 hover:bg-emerald-500/[0.14]',
    icon: 'border-emerald-400/35 bg-emerald-500/10 text-emerald-300 group-hover:border-emerald-300/55 group-hover:bg-emerald-500/20 group-hover:text-emerald-200',
    label: 'text-emerald-200/75 group-hover:text-emerald-100',
  },
};

/** Botão card tracejado (mesmo efeito visual dos slots «Adicionar» na aba Equipe). */
export function PainelDashedAddCardButton({
  label,
  onClick,
  className,
  icon,
  accent = 'sky',
  size = 'default',
}: {
  label: string;
  onClick: () => void;
  className?: string;
  /** Substitui o ícone + no círculo (ex.: smile.svg / pawn.svg / power.svg). */
  icon?: ReactNode;
  /** Cor alinhada ao ícone da secção (amber = smilies, cyan = peões). */
  accent?: DashedAddAccent;
  /** `compact` = altura de uma linha de input (grelha de smilies). */
  size?: 'default' | 'compact';
}) {
  const tone = ADD_ACCENT[accent];
  const compact = size === 'compact';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'group flex w-full items-center justify-center text-center',
        'border border-dashed transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
        tone.shell,
        compact
          ? 'min-h-[58px] flex-row gap-2.5 rounded-xl px-3 py-2'
          : 'flex-col gap-3 rounded-2xl px-6 py-8',
        className
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full transition-all duration-200 group-hover:scale-105',
          tone.icon,
          compact ? 'h-9 w-9' : 'h-12 w-12'
        )}
      >
        {icon ?? <Plus className={compact ? 'h-4 w-4' : 'h-6 w-6'} />}
      </span>
      <span
        className={cn(
          'font-inter font-medium transition-colors',
          tone.label,
          compact ? 'text-left text-xs leading-tight' : 'text-xs'
        )}
      >
        {label}
      </span>
    </button>
  );
}

type SmilieCodesGridProps = {
  codes: string[];
  onChange: (codes: string[]) => void;
  emptyHint?: string;
};

/** Grelha de smilies: botão full-width vazio; ao lado dos inputs quando há smilies. */
export function SmilieCodesGrid({ codes, onChange, emptyHint }: SmilieCodesGridProps) {
  const add = () => onChange([...codes, '']);
  const remove = (index: number) => onChange(codes.filter((_, i) => i !== index));
  const patch = (index: number, nextCode: string) => {
    const next = [...codes];
    next[index] = nextCode;
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {codes.length === 0 ? (
        <>
          <p className="font-inter text-sm text-slate-400">
            {emptyHint ??
              'Nenhum smiley ainda. Os códigos entram automaticamente pela API ao criar o power, ou adicione manualmente.'}
          </p>
          <PainelDashedAddCardButton
            label="Adicionar smiley"
            onClick={add}
            accent="amber"
            icon={<SmileNavIcon className="h-6 w-6" />}
          />
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {codes.map((code, index) => (
            <div
              key={index}
              className="flex min-h-[58px] items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2"
            >
              <GsImage
                code={code.trim() || 'smile'}
                className="flex h-10 w-10 shrink-0 items-center justify-center"
                imgClassName="max-h-9 max-w-9 object-contain"
              />
              <Input
                value={code}
                onChange={(e) => patch(index, e.target.value)}
                className={cn(painelInputClass, 'min-w-0 flex-1 font-mono text-xs')}
                placeholder="cool"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-xl text-rose-300 hover:bg-rose-500/15 hover:text-rose-100"
                onClick={() => remove(index)}
                aria-label={`Remover smiley ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <PainelDashedAddCardButton
            label="Adicionar smiley"
            onClick={add}
            accent="amber"
            size="compact"
            icon={<SmileNavIcon className="h-5 w-5" />}
          />
        </div>
      )}
    </div>
  );
}
